// Dev Console attachments — collect files from paste / drop / picker / folder
// (whole trees, thousands of files), and upload them as a "data package".
//
//   collect*()          → [{ file, relPath }]  (relPath keeps folder structure)
//   applySkips()        → drop node_modules/.git/… per live-config dev.attachments.skip
//   summarize()         → counts the tray shows
//   uploadAttachments() → { images, files, package } the /dev routes accept
//
// Small files go through one presigned PUT each (presign-batch, 6 in
// flight); large ones ride the resumable multipart path. The first few loose
// files stay first-class (`images[]`/`files[]`), everything else lands in the
// package: a prefix of objects + manifest.json the backend reads.
import {
  uploadFile, presignBatch, putObject, isUploadConfigured,
} from '../data/uploadService';
import { readDataTransfer, sanitizePath, sanitizeSeg } from '../utils/folders';

export const SMALL_FILE_BYTES = 8 * 1024 * 1024; // single PUT below this, multipart above
export const LOOSE_FIRST_CLASS = 12; // loose files sent as images[]/files[] before the rest go in the package

const IMAGE_RE = /^image\//;
export const isImage = (file) => IMAGE_RE.test(file.type || '') || /\.(png|jpe?g|gif|webp|bmp|heic|svg)$/i.test(file.name || '');

// ---- collecting -------------------------------------------------------------------

export function collectFromFileList(list) {
  return Array.from(list || []).filter(Boolean).map((f) => ({ file: f, relPath: f.webkitRelativePath || f.name }));
}

export async function collectFromDataTransfer(dt) {
  if (!dt) return [];
  return readDataTransfer(dt);
}

// Paste: files (screenshots, copied files), or an <img> in pasted HTML
// (copying an image from a web page / chat app gives HTML, not a file).
// Returns { entries, note } — note explains what could not be attached.
export async function collectFromPaste(e) {
  const cd = e?.clipboardData;
  if (!cd) return { entries: [], note: '' };
  const files = [];
  for (const it of Array.from(cd.items || [])) {
    if (it.kind === 'file') { const f = it.getAsFile(); if (f) files.push(f); }
  }
  // Safari sometimes exposes pasted files only here.
  if (!files.length && cd.files?.length) files.push(...Array.from(cd.files));
  if (files.length) {
    return { entries: files.map((f, i) => { const file = stampName(f, i); return { file, relPath: file.name }; }), note: '' };
  }
  const html = cd.getData && cd.getData('text/html');
  if (html) {
    const srcs = [...html.matchAll(/<img[^>]+src=["']([^"']+)["']/gi)].map((m) => m[1]).slice(0, 10);
    const entries = [];
    const unreachable = [];
    for (const src of srcs) {
      // eslint-disable-next-line no-await-in-loop
      const f = await fetchImage(src);
      if (f) entries.push({ file: f, relPath: f.name }); else unreachable.push(src);
    }
    if (entries.length || unreachable.length) {
      return {
        entries,
        note: unreachable.length ? `${unreachable.length} pasted image${unreachable.length > 1 ? 's' : ''} could not be fetched from the source site — save and attach the file instead.` : '',
        text: !entries.length && unreachable.length ? unreachable.map((u) => `(image: ${u})`).join('\n') : '',
      };
    }
  }
  return { entries: [], note: '' };
}

// Pasted screenshots arrive as "image.png" (or unnamed) — stamp them.
function stampName(f, i) {
  if (f.name && f.name !== 'image.png' && f.name !== 'image.jpeg') return f;
  const ext = (f.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
  return new File([f], `pasted-${new Date().toISOString().replace(/[:.]/g, '-')}-${i + 1}.${ext}`, { type: f.type });
}

async function fetchImage(src) {
  try {
    if (src.startsWith('data:')) {
      const res = await fetch(src);
      const blob = await res.blob();
      const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
      return new File([blob], `pasted-${Date.now()}.${ext}`, { type: blob.type || 'image/png' });
    }
    const res = await fetch(src, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!IMAGE_RE.test(blob.type)) return null;
    const base = src.split('?')[0].split('/').pop() || 'image';
    const name = /\.(png|jpe?g|gif|webp)$/i.test(base) ? base : `${base}.${(blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`;
    return new File([blob], name, { type: blob.type });
  } catch {
    return null;
  }
}

// File System Access API folder pick (Chromium). Returns entries, or null
// when unsupported/blocked so the caller can fall back to a webkitdirectory
// input. Throws nothing on user cancel (returns []).
export async function pickFolderEntries() {
  if (typeof window === 'undefined' || !window.showDirectoryPicker) return null;
  try {
    const dir = await window.showDirectoryPicker({ mode: 'read' });
    const out = [];
    await readDirHandle(dir, `${dir.name}/`, out);
    return out;
  } catch (err) {
    if (err && err.name === 'AbortError') return [];
    return null;
  }
}

async function readDirHandle(dir, prefix, out) {
  // eslint-disable-next-line no-restricted-syntax
  for await (const entry of dir.values()) {
    if (entry.kind === 'file') {
      // eslint-disable-next-line no-await-in-loop
      out.push({ file: await entry.getFile(), relPath: `${prefix}${entry.name}` });
    } else if (entry.kind === 'directory') {
      // eslint-disable-next-line no-await-in-loop
      await readDirHandle(entry, `${prefix}${entry.name}/`, out);
    }
  }
}

// ---- shaping ---------------------------------------------------------------------

const DEFAULT_SKIP = ['node_modules', '.git', '.DS_Store', 'dist', 'build', '.next', '__pycache__', '.venv', 'venv', '.cache'];

// Drop vendored/derived trees and OS litter; dedupe by relPath+size.
export function applySkips(entries, skip = DEFAULT_SKIP) {
  const skipSet = new Set((skip || DEFAULT_SKIP).map((s) => String(s).toLowerCase()));
  const seen = new Set();
  const kept = [];
  let skipped = 0;
  for (const en of entries) {
    const segs = en.relPath.split('/').filter(Boolean);
    if (segs.some((seg) => skipSet.has(seg.toLowerCase()))) { skipped += 1; continue; }
    if (en.file.size === 0 && !/\.(gitkeep|keep)$/i.test(en.relPath)) { skipped += 1; continue; }
    const sig = `${en.relPath}\u0000${en.file.size}`;
    if (seen.has(sig)) continue;
    seen.add(sig);
    kept.push(en);
  }
  return { kept, skipped };
}

export function summarize(entries) {
  const folders = new Set();
  let bytes = 0; let images = 0; let docs = 0; let other = 0;
  const byExt = new Map();
  for (const { file, relPath } of entries) {
    bytes += file.size;
    if (relPath.includes('/')) folders.add(relPath.split('/')[0]);
    const ext = (relPath.split('.').pop() || '').toLowerCase();
    byExt.set(ext, (byExt.get(ext) || 0) + 1);
    if (isImage(file)) images += 1;
    else if (/^(md|markdown|txt|pdf|docx|csv|json|ya?ml|html?)$/.test(ext)) docs += 1;
    else other += 1;
  }
  return { count: entries.length, bytes, folders: [...folders], images, docs, other, byExt: [...byExt.entries()].sort((a, b) => b[1] - a[1]) };
}

export function fmtBytes(n) {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(u.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / 1024 ** i).toFixed(i ? 1 : 0)} ${u[i]}`;
}

// Check against the live-config limits (from /dev/status). Returns an error
// string or ''.
export function checkLimits(entries, limits = {}) {
  const maxFiles = Number(limits.maxFiles) || 5000;
  const maxBytes = Number(limits.maxBytes) || 2 * 1024 * 1024 * 1024;
  const maxFileBytes = Number(limits.maxFileBytes) || 200 * 1024 * 1024;
  if (entries.length > maxFiles) return `${entries.length} files — the limit per message is ${maxFiles}. Attach a subfolder, or split the package.`;
  const total = entries.reduce((n, e) => n + e.file.size, 0);
  if (total > maxBytes) return `${fmtBytes(total)} — the limit per message is ${fmtBytes(maxBytes)}.`;
  const big = entries.find((e) => e.file.size > maxFileBytes);
  if (big) return `${big.relPath} is ${fmtBytes(big.file.size)} — files over ${fmtBytes(maxFileBytes)} are too large.`;
  return '';
}

// ---- uploading ----------------------------------------------------------------------

// entries → { images: [{key}], files: [{key,name,type,size}], package: {...}|null }
// onProgress({ done, total, bytesDone, bytesTotal, current })
export async function uploadAttachments(entries, { prefix, onProgress, signal } = {}) {
  if (!entries.length) return { images: [], files: [], package: null };
  if (!isUploadConfigured()) throw new Error('Upload API not configured');
  const batch = `${Date.now()}`;
  const loose = entries.filter((e) => !e.relPath.includes('/'));
  const firstClass = loose.slice(0, LOOSE_FIRST_CLASS);
  const packaged = entries.filter((e) => !firstClass.includes(e));
  const total = entries.length;
  const bytesTotal = entries.reduce((n, e) => n + e.file.size, 0);
  let done = 0;
  const loaded = new Map();
  const report = (current) => onProgress?.({
    done, total, bytesTotal, current,
    bytesDone: [...loaded.values()].reduce((n, v) => n + v, 0),
  });

  // Plan keys up front.
  const jobs = [];
  for (const [i, e] of firstClass.entries()) {
    jobs.push({ e, key: `${prefix}${batch}-${i + 1}-${sanitizeSeg(e.file.name)}`, firstClass: true });
  }
  const pkgPrefix = `${prefix}pkg-${batch}/`;
  for (const e of packaged) {
    jobs.push({ e, key: `${pkgPrefix}files/${sanitizePath(e.relPath)}`, firstClass: false });
  }

  // Presign all small files in batches; large ones use multipart.
  const small = jobs.filter((j) => j.e.file.size < SMALL_FILE_BYTES);
  const large = jobs.filter((j) => j.e.file.size >= SMALL_FILE_BYTES);
  const urlByKey = new Map((await presignBatch(small.map((j) => ({ key: j.key, contentType: j.e.file.type || 'application/octet-stream' })))).map((u) => [u.key, u.url]));

  const runSmall = async (j) => {
    const url = urlByKey.get(j.key);
    if (!url) throw new Error(`no upload URL for ${j.e.relPath}`);
    await putObject(url, j.e.file, { signal, onProgress: (l) => { loaded.set(j.key, Math.min(l, j.e.file.size)); report(j.e.relPath); } });
    loaded.set(j.key, j.e.file.size); done += 1; report(j.e.relPath);
  };
  const runLarge = async (j) => {
    await uploadFile(j.e.file, {
      key: j.key, contentType: j.e.file.type || 'application/octet-stream', signal,
      onProgress: (p) => { loaded.set(j.key, p * j.e.file.size); report(j.e.relPath); },
    });
    loaded.set(j.key, j.e.file.size); done += 1; report(j.e.relPath);
  };
  const failures = [];
  const pool = async (list, n, fn) => {
    const q = [...list];
    const worker = async () => {
      for (let j = q.shift(); j; j = q.shift()) {
        if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
        // eslint-disable-next-line no-await-in-loop
        try { await fn(j); } catch (err) { if (err?.name === 'AbortError') throw err; failures.push({ path: j.e.relPath, error: err.message }); done += 1; }
      }
    };
    await Promise.all(Array.from({ length: Math.min(n, list.length) }, worker));
  };
  report('');
  await Promise.all([pool(small, 6, runSmall), pool(large, 2, runLarge)]);
  if (failures.length === total) throw new Error(`upload failed: ${failures[0]?.error || 'unknown error'}`);

  const okJobs = jobs.filter((j) => !failures.some((f) => f.path === j.e.relPath));
  const images = okJobs.filter((j) => j.firstClass && isImage(j.e.file)).map((j) => ({ key: j.key }));
  const files = okJobs.filter((j) => j.firstClass && !isImage(j.e.file)).map((j) => ({ key: j.key, name: j.e.file.name, type: j.e.file.type || '', size: j.e.file.size }));
  let pkg = null;
  const pkgJobs = okJobs.filter((j) => !j.firstClass);
  if (pkgJobs.length) {
    const folders = new Set(pkgJobs.filter((j) => j.e.relPath.includes('/')).map((j) => j.e.relPath.split('/')[0]));
    const allInOneFolder = folders.size === 1 && pkgJobs.every((j) => j.e.relPath.includes('/'));
    const name = allInOneFolder ? [...folders][0] : `package-${new Date().toISOString().slice(0, 10)}`;
    const manifest = {
      id: `pkg-${batch}`, name, createdAt: Date.now(),
      bytes: pkgJobs.reduce((n, j) => n + j.e.file.size, 0),
      files: pkgJobs.map((j) => ({ path: j.e.relPath, key: j.key, size: j.e.file.size, type: j.e.file.type || '' })),
    };
    const manifestKey = `${pkgPrefix}manifest.json`;
    const blob = new File([JSON.stringify(manifest)], 'manifest.json', { type: 'application/json' });
    const [{ url }] = await presignBatch([{ key: manifestKey, contentType: 'application/json' }]);
    await putObject(url, blob, { signal });
    pkg = { key: manifestKey, id: manifest.id, name, files: manifest.files.length, bytes: manifest.bytes, folders: folders.size };
  }
  return { images, files, package: pkg, failures };
}
